import dayjs, { Dayjs } from "dayjs"
import { sumBy } from 'lodash';
import { Batch, BatchProductionView, GetAnalysesAndActionsForTankQuery, Tank } from "types/graphql"
import isBetween from 'dayjs/plugin/isBetween' // ES 2015
import { TANK_MONITORED_ACTIONS, VALUES_INVERTED_SCALE } from "./Defaults";
import { GridFilterModel } from "@mui/x-data-grid";
import { SxProps, Theme } from "@mui/material";
import { evaluateFormula } from "src/lib/formulaEvaluator";
import { useDateStore } from "src/lib/store";
dayjs.extend(isBetween);

export const getCurrentStadePLInBatchProductions = (data: Partial<BatchProductionView>[], date = dayjs()) => {
    const item = data.find(item => date.isSame(item.date, 'day'))
    if (item)
        return item.stadePL
    else
        return null
}

//https://stackoverflow.com/a/53577159/1437016
export const getStandardDeviation = (array) => {
    const n = array.length
    const mean = array.reduce((a, b) => a + b) / n
    return Math.sqrt(array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / (n -1))
}

export const standardDeviationWithPopulation = (arr, usePopulation = false) => {
    const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length;
    return Math.sqrt(
      arr.reduce((acc, val) => acc.concat((val - mean) ** 2), []).reduce((acc, val) => acc + val, 0) /
        (arr.length - (usePopulation ? 0 : 1))
    );
  };
  
//https://stackoverflow.com/a/51840444/1437016
//Underscorejs equivalent to reduce an array by a shared key value
export const indexBy = (list, iteratee, _context = null) => {
    return list.reduce((map, obj) => {
        const key = typeof iteratee === 'string' ? obj[iteratee] : iteratee.call(_context, obj);
        map[key] = obj;
        return map;
    }, {});
}
//https://stackoverflow.com/a/51840444/1437016
//Underscorejs equivalent to reduce an array by a shared key value
export const groupBy = <T>(
    list: T[], 
    iteratee: string | ((obj: T) => string | number), 
    _context: any = null
): Record<string, T & { data: T[] }> => {
    return list.reduce((map, obj) => {
        const key = typeof iteratee === 'string' ? obj[iteratee] : iteratee.call(_context, obj);
        if(!map[key])
            map[key] = {...obj, data: [obj]};
        else
            map[key].data.push(obj);
        return map;
    }, {} as Record<string, T & { data: T[] }>);
}

//Took from datagrid @mui/x-data-grid/hooks/features/filter/gridFilterUtils.js
export const removeDiacritics = value => {
  if (typeof value === 'string') {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return value;
};

export const arrayToObjectByProps = (array) => {
    return array.reduce((acc, obj) => {
        Object.keys(obj).forEach(key => {
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(obj[key] || "NA");
        });
        return acc;
      }, {});
}
export const objectToArrayByPropsWithStadePL = (obj: Record<string, any[]>, initialStadePL: number): any[] => {
const keys = Object.keys(obj);
const length = obj[keys[0]].length;
const result = [];

for (let i = 0; i < length; i++) {
    const item: Record<string, any> = { stadePL: initialStadePL + i };
    keys.forEach(key => {
    item[key] = obj[key][i];
    });
    result.push(item);
}

return result;
}
  
export const uniquePath = (list, path) => {
    return list.reduce((acc, item) => {
        const id = item[path];
        if (id && acc.indexOf(id) == -1)
          acc.push(id)
        return acc;
      }, [])
}

/**
 * Exclude NaN, null or 0 values from array
 * @param array 
 * @param getter function to retrieve value
 * @returns number[]
 */
export const reduceToPositiveValues = (array: any[], getter: (item:any) => number): number[] => {
  return array.reduce((acc, item) => {
    const value = getter(item)
    //Remove 0 or null value
    if(value > 0)
      acc.push(value); 
    return acc;
  }, [])
}

/**
 * Sum by excluding NaN, null or 0 values from array
 * @param array 
 * @param getter function to retrieve value
 * @returns number
 */
export const sumPositiveValues = (array: any[], getter: (item:any) => number): number => {
  return array.reduce((acc, item) => {
    const value = getter(item)
    //Remove 0 or null value
    if(value > 0)
      acc+= value; 
    return acc;
  }, 0)
}

export const estimateRequestTime = ( {batch_length, future_length, num_generations, num_individuals, type} ) => {
    let elapsed_time_server, elapsed_time_pc;
  
    if(type == "predict-optimal"){
      elapsed_time_server = 0.0000 + -6.5121 * future_length + 73.9526 * num_generations + 48.6454 * num_individuals
      elapsed_time_pc = 0.0000 + -16.9460 * future_length + 57.4913 * num_generations + 43.8214 * num_individuals
    }
    else {
      elapsed_time_pc = 15.0520 + 0.1164 * batch_length + 0.0000 * future_length + 0.0000 * num_generations + 0.0000 * num_individuals
      elapsed_time_server = 25.6125 + 0.0443 * batch_length + 0.0000 * future_length + 0.0000 * num_generations + 0.0000 * num_individuals
    }
    return {server: elapsed_time_server, pc: elapsed_time_pc}
}

const aggregateDataByPeriod = (data, datePeriod, dateFormat, columns, columnsToCompute) => {
  const groupedData = groupBy(data, (batch) => dayjs(batch.harvest?.createdAt).format(dateFormat))
  return Object.keys(groupedData).map((key) => {
    const rows = groupedData[key];
    const date = (dayjs(key, dateFormat)).add(12, "hours") //Hack to get the right timezone
    const aggregatedRow = { 
      id: key, 
      date: date.toDate(), 
      year: date.get("year"), 
      month: date.get("month") +1, 
      datePeriod, 
      batches: rows.data,
      batchesOfPeriod: data.filter(batch => {
        // A batch can start on N or PG (historically)
        const start = batch.report?.dateBatchTransferN || batch.report?.dateBatchTransferNPG,
              //and can end anywhere
              end = batch.report?.dateBatchTransferGA || batch.report?.dateBatchTransferPGG || batch.report?.dateBatchTransferNPG;
        return date.isBetween(dayjs(start), dayjs(end)) || dayjs(start).isSame(date, datePeriod)  || dayjs(end).isSame(date, datePeriod)
        // dayjs(batch.report?.dateBatchTransferNPG).isSame(date, datePeriod) || 
        // dayjs(batch.report?.dateBatchTransferPG).isSame(date, datePeriod) || 
        // dayjs(batch.report?.dateBatchTransferGA).isSame(date, datePeriod)
      }
      )
    };

    columns.forEach(column => {
      //From greater to lesser value
      const _data = reduceToPositiveValues(rows.data, (item) => column.valueGetter(null, item)).sort((a,b) => b - a)

      if (column.valueGetter && column.type == "number")
          aggregatedRow[column.field+"_avg"] = _data.length ? _data.reduce((acc, value) => acc += value, 0) / _data.length : null;
      if (column?.aggregation?.indexOf("sum") >= 0)
          aggregatedRow[column.field+"_sum"] = _data.length ? _data.reduce((acc, value) => acc += value, 0) : null;
      if (column?.aggregation?.indexOf("best") >= 0){
        const reversedOrder = VALUES_INVERTED_SCALE.indexOf(column.field) >= 0;
        aggregatedRow[column.field+"_best"] = _data[reversedOrder ? (_data.length - 1): 0];
      }
      if (column?.aggregation?.indexOf("count") >= 0){
        aggregatedRow[column.field+"_count"] = _data.length;
      }
    });
    columnsToCompute.forEach(column => {
      aggregatedRow[column.field] = column.valueGetter(null, aggregatedRow);
    });

    return aggregatedRow;
  }).sort((a,b) => b.date - a.date);
};

export const flatBatchesArrayWithValueGetter = (data, columns, shouldAddDataToExistingRow = false) => {
  return data.map(row => {
    let aggregatedRow = shouldAddDataToExistingRow ? {...row} : {
      id: row.name
    }
    columns.forEach(column => {
      //If the column is computed from data with a formula
      if (column.formula){
        aggregatedRow[column.field] = evaluateFormula(column.formula, row, columns, data, null, true)
      }
      else if (column.valueGetter && (["number","date"].indexOf(column.type) >= 0)){
        aggregatedRow[column.field] = column.valueGetter(null, row) || 0
      }
    });
    // customColumns.forEach(col => {
    //   if (col.aggregation && col.valueGetter) {
    //     const value = col.valueGetter(null, row);
    //     aggregatedRow[col.field] = value
        // // Add to aggregations
        // col.aggregation.forEach(agg => {
        //   if (agg === 'sum') {
        //     if (typeof value === 'number') {
        //       row[`${col.field}_sum`] = (row[`${col.field}_sum`] || 0) + value;
        //     }
        //   } else if (agg === 'avg') {
        //     row[`${col.field}_values`] = row[`${col.field}_values`] || [];
        //     if (value !== null && value !== undefined) {
        //       row[`${col.field}_values`].push(value);
        //       row[`${col.field}_avg`] = row[`${col.field}_values`].reduce((a, b) => a + b, 0) / row[`${col.field}_values`].length;
        //     }
        //   } else if (agg === 'best') {
        //     if (value !== null && value !== undefined) {
        //       if (row[`${col.field}_best`] === undefined || value > row[`${col.field}_best`]) {
        //         row[`${col.field}_best`] = value;
        //       }
        //     }
        //   }
        // });
    //   }
    // });
    return aggregatedRow;
  }).sort((a,b) => b.harvestDate - a.harvestDate)
}
// Function to aggregate data based on the selected view mode
export const aggregateData = (batches, columns, columnsToCompute = null) => {
  const flatBatches = flatBatchesArrayWithValueGetter(batches, columns);
  const monthlyAggregated = aggregateDataByPeriod(batches, 'month', 'YYYY-MM', columns, columnsToCompute);
  const yearlyAggregated = aggregateDataByPeriod(batches, 'year', 'YYYY', columns, columnsToCompute);


  return [flatBatches, monthlyAggregated, yearlyAggregated];
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}


export const lowercaseFirstLetter = (string) => {
  return string.charAt(0).toLowerCase() + string.slice(1);
}

/**
 * Convert a filter model date selection to an array of usable Dayjs
 * @param filterModel Grid filter model, retreived by apiRef
 * @param timeField Date field to check on col def
 * @returns 
 */
export const convertFilterToRange = (filterModel: GridFilterModel, timeField: String): [Dayjs,Dayjs] => {
  if (filterModel?.items)
    return filterModel.items.reduce((dates, item): [Dayjs, Dayjs] => {
      if (item.field == timeField) {
        let index;
        if (["after", "sameOrAfter"].indexOf(item.operator) > -1)
          index = 0
        else if (["before", "sameOrBefore"].indexOf(item.operator) > -1)
          index = 1;
        if (typeof index != "undefined")
          dates[index] = dayjs(item.value)
      }
      return dates;
    }, [null, null])
  else
    return [null, null]
}

//https://mui.com/material-ui/react-avatar/#letter-avatars
export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

export const stringAvatar = (name: string, sx?: SxProps<Theme>) => {
  return {
    sx: {
      bgcolor: stringToColor(name),
      ...sx
    },
    children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
}


export const getTankDailyActions = (tank: Partial<GetAnalysesAndActionsForTankQuery["tank"]>, idBatch: number) => {
  const date = useDateStore(state => state.getDayJSDate());
  const isAM = !date.isSame(dayjs(), "day") ? false : dayjs().hour() < 13;
  if(tank && !idBatch)
    return {missingActions: [], isAM, completedActions :[], allActions :[]}
  //If it's not monday, remove biometry
  const allActions = TANK_MONITORED_ACTIONS.filter(actionKey => (actionKey == "biometricAnalysis") ? date.day() === 1 : true );
  const missingActions = allActions.filter(actionKey => !tank?.[actionKey])
  const completedActions = allActions.filter(actionKey => tank?.[actionKey])
  return {missingActions, isAM, completedActions, allActions}
}
